import { Injectable } from '@angular/core';
import { BillingStrategy, ProductPricing } from '@galaxy/billing';
import { EnvironmentService } from '@galaxy/core';
import { ItemWhitelabelService } from '@galaxy/inventory-ui';
import { AppPrices } from '@galaxy/marketplace-apps';
import { ListAppsAndAddonsActivationsStatusesForBusinessResponseAppsAndAddonsActivationStatusesInterface as ActivationStatusesInterface } from '@vendasta/accounts/legacy';
import { Order as SalesOrder } from '@vendasta/sales-orders';
import { ResellerItem } from '@vendasta/store';
import { combineLatest, Observable } from 'rxjs';
import { distinctUntilChanged, map, shareReplay, switchMap, take } from 'rxjs/operators';
import { resellerArrayToMap } from '../../core/reseller/utils';
import { ActivationParamService } from '../core/activation-param.service';
import { ActivationPricingService } from '../core/activation-pricing.service';
import { ActivationService } from '../core/activation.service';
import { AppIdToEditionIdToEditionMap, SelectedItems } from '../core/interface';
import { ProductOrderFormStoreService } from '../core/product-order-form.store.service';
import { OrderConfirmationItem } from './interface';
import {
  convertAppIdEditionsMapToEditionIdEditionMap,
  convertResellerItemsToSummaries,
  filterOutActiveSelectedItems,
} from './utils';

@Injectable()
export class OrderConfirmationService {
  readonly orderConfirmationItems$: Observable<OrderConfirmationItem[]>;
  readonly hidePricing$: Observable<boolean>;
  readonly productIsInstantBilledMap$: Observable<Map<string, BillingStrategy>>;
  readonly userCanEditBillingInfo$: Observable<boolean>;
  readonly billingInfoConfigured$: Observable<boolean>;
  readonly editionIdToEditionMap$: Observable<AppIdToEditionIdToEditionMap>;

  readonly retailProductPricingMap$: Observable<Map<string, ProductPricing>>;
  readonly appPriceMap$: Observable<Map<string, AppPrices>>;
  readonly partnerCurrency$: Observable<string>;
  readonly marketCurrency$: Observable<string>;

  readonly salesOrder$: Observable<SalesOrder>;

  private selectedItems$: Observable<SelectedItems>;
  private resellerItemMap$: Observable<Map<string, ResellerItem>>;
  private appStatus$: Observable<ActivationStatusesInterface[]>;

  constructor(
    private activationService: ActivationService,
    private activationPricingService: ActivationPricingService,
    private activationParamsService: ActivationParamService,
    private productOrderFormService: ProductOrderFormStoreService,
    private environmentService: EnvironmentService,
    private itemWhitelabelService: ItemWhitelabelService,
  ) {
    this.hidePricing$ = this.activationPricingService.hidePricing$;
    this.userCanEditBillingInfo$ = this.activationPricingService.userCanEditBillingInfo$;
    this.billingInfoConfigured$ = this.activationPricingService.billingInfoConfigured$;
    this.salesOrder$ = this.productOrderFormService.cachedOrder$;
    this.appStatus$ = this.activationService.appStatuses$;
    this.retailProductPricingMap$ = this.activationPricingService.retailProductPricingMap$;
    this.appPriceMap$ = this.activationPricingService.appPriceMap$;
    this.partnerCurrency$ = this.activationPricingService.partnerCurrency$;
    this.marketCurrency$ = this.activationPricingService.marketCurrency$;

    this.resellerItemMap$ = this.activationService.activatableResellerItems$.pipe(
      map((resellerItems) => {
        return resellerArrayToMap(resellerItems);
      }),
    );

    this.selectedItems$ = combineLatest([
      this.activationService.selectedItems$,
      this.resellerItemMap$,
      this.appStatus$,
      this.activationService.activations$,
    ]).pipe(
      map(([selectedItems, resellerItemMap, appStatus, activations]) => {
        return filterOutActiveSelectedItems(selectedItems, appStatus, resellerItemMap, activations);
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );

    this.productIsInstantBilledMap$ = combineLatest([
      this.resellerItemMap$,
      this.activationService.pricingMap$,
      this.selectedItems$,
    ]).pipe(
      map(([resellerItemMap, pricingMap, items]) => {
        return createProductBillingStrategyMap(items, pricingMap, resellerItemMap);
      }),
      distinctUntilChanged(),
      shareReplay(1),
    );

    const orderConfirmationItems$ = combineLatest([
      this.selectedItems$,
      this.resellerItemMap$,
      this.appPriceMap$,
      this.partnerCurrency$,
      this.retailProductPricingMap$,
      this.marketCurrency$,
    ]).pipe(
      map(([selectedItems, resellerItemMap, appPriceMap, partnerCurrency, productPricingMap, marketCurrency]) => {
        return convertResellerItemsToSummaries(
          selectedItems,
          resellerItemMap,
          appPriceMap,
          partnerCurrency,
          this.environmentService.getEnvironment(),
          productPricingMap,
          marketCurrency,
        );
      }),
    );
    this.orderConfirmationItems$ = combineLatest([
      orderConfirmationItems$,
      this.activationParamsService.marketId$,
    ]).pipe(
      take(1),
      switchMap(([orderItems, marketId]) => {
        return this.itemWhitelabelService.whitelabelItems$<OrderConfirmationItem>(orderItems, marketId);
      }),
    );

    this.editionIdToEditionMap$ = this.activationService.editionsMap$.pipe(
      map((edMap) => convertAppIdEditionsMapToEditionIdEditionMap(edMap)),
    );
  }

  setContractStartDate(startDate: Date): void {
    this.salesOrder$.pipe(take(1)).subscribe((order) => {
      this.productOrderFormService.cacheOrder({ ...order, requestedActivation: startDate } as SalesOrder);
    });
  }

  clearSalesOrderCache(): void {
    this.productOrderFormService.clearOrderCache();
  }
}

export function createProductBillingStrategyMap(
  items: SelectedItems,
  pricingMap: Map<string, ProductPricing>,
  resellerItemMap: Map<string, ResellerItem>,
): Map<string, BillingStrategy> {
  if (!pricingMap || pricingMap.size <= 0 || !items || items.size <= 0) {
    return;
  }
  const keys = Array.from(items.keys());
  const productIsInstantBilledMap = new Map<string, BillingStrategy>();
  keys.forEach((key) => {
    const resellerItem = resellerItemMap.get(key);
    if (resellerItem) {
      const productPricing = pricingMap.get(resellerItem.billingId);
      if (productPricing) {
        productIsInstantBilledMap.set(key, productPricing.strategy);
      }
    }
  });
  return productIsInstantBilledMap;
}
